import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './styles/ConnectAuth.css';

export default function ConnectAuth() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Registration form state
  const [registerData, setRegisterData] = useState({
    fullName: '',
    gender: '',
    birthDate: '',
    email: '',
    password: '',
    confirmPassword: '',
    countryOfResidence: '',
    currentLocation: '',
    agreeToTerms: false
  });

  const genderOptions = [
    'Male',
    'Female',
    'Non-binary',
    'Genderqueer',
    'Genderfluid',
    'Agender',
    'Two-Spirit',
    'Prefer not to say',
    'Other'
  ];

  const countries = [
    'Antigua and Barbuda', 'Aruba', 'Bahamas', 'Barbados', 'Belize',
    'Bermuda', 'British Virgin Islands', 'Cayman Islands', 'Cuba',
    'Curaçao', 'Dominica', 'Dominican Republic', 'Grenada', 'Guadeloupe',
    'Haiti', 'Jamaica', 'Martinique', 'Montserrat', 'Puerto Rico',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
    'Sint Maarten', 'Trinidad and Tobago', 'Turks and Caicos Islands',
    'US Virgin Islands', 'United States', 'Canada', 'United Kingdom',
    'France', 'Netherlands', 'Germany', 'Spain', 'Other'
  ];

  const validateLogin = () => {
    const newErrors = {};
    if (!loginData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(loginData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!loginData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRegister = () => {
    const newErrors = {};

    if (!registerData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!registerData.gender) {
      newErrors.gender = 'Please select your gender';
    }

    if (!registerData.birthDate) {
      newErrors.birthDate = 'Birth date is required';
    } else {
      const birthDate = new Date(registerData.birthDate);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        newErrors.birthDate = 'You must be at least 18 years old';
      }
    }

    if (!registerData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(registerData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!registerData.password) {
      newErrors.password = 'Password is required';
    } else if (registerData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!registerData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (registerData.password !== registerData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!registerData.countryOfResidence) {
      newErrors.countryOfResidence = 'Country of residence is required';
    }

    if (!registerData.currentLocation.trim()) {
      newErrors.currentLocation = 'Current location is required';
    }

    if (!registerData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRegisterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    if (validateLogin()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        // Store mock user data
        localStorage.setItem('connectUser', JSON.stringify({
          email: loginData.email,
          isLoggedIn: true,
          onboardingComplete: false
        }));
        navigate('/connect/profile');
      }, 1000);
    }
  };

  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    if (validateRegister()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        // Store mock user data
        localStorage.setItem('connectUser', JSON.stringify({
          ...registerData,
          isLoggedIn: true,
          onboardingComplete: false
        }));
        navigate('/connect/profile');
      }, 1000);
    }
  };

  return (
    <div className="connect-auth-page">
      {/* Background decoration */}
      <div className="connect-auth-bg">
        <div className="connect-auth-bg-gradient"></div>
        <div className="connect-auth-bg-pattern"></div>
      </div>

      <div className="connect-auth-container">
        {/* Logo / Branding */}
        <div className="connect-auth-branding">
          <Link to="/" className="connect-auth-logo-link">
            <img 
              src={process.env.PUBLIC_URL + '/assets/logo.png'} 
              alt="OutCaribbean" 
              className="connect-auth-logo"
            />
          </Link>
          <h1 className="connect-auth-title">Connectivity App</h1>
          <p className="connect-auth-subtitle">
            Connect with LGBTQ+ travelers across the Caribbean
          </p>
        </div>

        {/* Auth Card */}
        <div className="connect-auth-card">
          {/* Tab Switcher */}
          <div className="connect-auth-tabs">
            <button
              className={`connect-auth-tab ${isLogin ? 'active' : ''}`}
              onClick={() => { setIsLogin(true); setErrors({}); }}
            >
              Sign In
            </button>
            <button
              className={`connect-auth-tab ${!isLogin ? 'active' : ''}`}
              onClick={() => { setIsLogin(false); setErrors({}); }}
            >
              Sign Up
            </button>
          </div>

          {/* Login Form */}
          {isLogin && (
            <form className="connect-auth-form" onSubmit={handleLoginSubmit}>
              <div className="connect-field-group">
                <label className="connect-field-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  className={`connect-field-input ${errors.email ? 'error' : ''}`}
                  placeholder="your@email.com"
                />
                {errors.email && <span className="connect-field-error">{errors.email}</span>}
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Password</label>
                <input
                  type="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  className={`connect-field-input ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                />
                {errors.password && <span className="connect-field-error">{errors.password}</span>}
              </div>

              <div className="connect-forgot-link">
                <a href="#">Forgot password?</a>
              </div>

              <button
                type="submit"
                className="connect-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          {/* Registration Form */}
          {!isLogin && (
            <form className="connect-auth-form" onSubmit={handleRegisterSubmit}>
              <div className="connect-field-group">
                <label className="connect-field-label">Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  value={registerData.fullName}
                  onChange={handleRegisterChange}
                  className={`connect-field-input ${errors.fullName ? 'error' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.fullName && <span className="connect-field-error">{errors.fullName}</span>}
              </div>

              <div className="connect-field-row">
                <div className="connect-field-group">
                  <label className="connect-field-label">Gender</label>
                  <select
                    name="gender"
                    value={registerData.gender}
                    onChange={handleRegisterChange}
                    className={`connect-field-select ${errors.gender ? 'error' : ''}`}
                  >
                    <option value="">Select gender</option>
                    {genderOptions.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  {errors.gender && <span className="connect-field-error">{errors.gender}</span>}
                </div>

                <div className="connect-field-group">
                  <label className="connect-field-label">Birth Date</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={registerData.birthDate}
                    onChange={handleRegisterChange}
                    className={`connect-field-input ${errors.birthDate ? 'error' : ''}`}
                  />
                  {errors.birthDate && <span className="connect-field-error">{errors.birthDate}</span>}
                </div>
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  className={`connect-field-input ${errors.email ? 'error' : ''}`}
                  placeholder="your@email.com"
                />
                {errors.email && <span className="connect-field-error">{errors.email}</span>}
              </div>

              <div className="connect-field-row">
                <div className="connect-field-group">
                  <label className="connect-field-label">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    className={`connect-field-input ${errors.password ? 'error' : ''}`}
                    placeholder="Min. 8 characters"
                  />
                  {errors.password && <span className="connect-field-error">{errors.password}</span>}
                </div>

                <div className="connect-field-group">
                  <label className="connect-field-label">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    className={`connect-field-input ${errors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm password"
                  />
                  {errors.confirmPassword && <span className="connect-field-error">{errors.confirmPassword}</span>}
                </div>
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Country of Residence</label>
                <select
                  name="countryOfResidence"
                  value={registerData.countryOfResidence}
                  onChange={handleRegisterChange}
                  className={`connect-field-select ${errors.countryOfResidence ? 'error' : ''}`}
                >
                  <option value="">Select country</option>
                  {countries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {errors.countryOfResidence && <span className="connect-field-error">{errors.countryOfResidence}</span>}
              </div>

              <div className="connect-field-group">
                <label className="connect-field-label">Current Location</label>
                <input
                  type="text"
                  name="currentLocation"
                  value={registerData.currentLocation}
                  onChange={handleRegisterChange}
                  className={`connect-field-input ${errors.currentLocation ? 'error' : ''}`}
                  placeholder="City, Country"
                />
                {errors.currentLocation && <span className="connect-field-error">{errors.currentLocation}</span>}
              </div>

              <div className="connect-terms-group">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={registerData.agreeToTerms}
                  onChange={handleRegisterChange}
                  className="connect-terms-checkbox"
                  id="agreeToTerms"
                />
                <label htmlFor="agreeToTerms" className="connect-terms-text">
                  I agree to the <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>
                </label>
              </div>
              {errors.agreeToTerms && <span className="connect-field-error">{errors.agreeToTerms}</span>}

              <button
                type="submit"
                className="connect-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}
        </div>

        {/* Back to site link */}
        <div className="connect-auth-footer">
          <Link to="/" className="connect-back-link">
            ← Back to OutCaribbean
          </Link>
        </div>
      </div>
    </div>
  );
}
